// =================================================================================
// src/services/adminApiClient.js
// This file is dedicated to all admin-related API calls.
// =================================================================================
const API_BASE_URL = '/api';

/**
 * **ADMIN FUNCTION**
 * Uploads a file to the /admin/uploadCalendar endpoint.
 * @param {File} file The file to upload.
 * @returns {Promise<object>} The backend's response.
 */
export const uploadCalendarFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
        const response = await fetch(`${API_BASE_URL}/admin/uploadCalendar`, {
            method: 'POST',
            body: formData,
            headers: { 'accept': 'application/json' },
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'File upload failed' }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("File upload failed:", e);
        throw e;
    }
};

/**
 * **ADMIN FUNCTION**
 * Updates table info.
 * @param {object} tableInfo The table information.
 * @returns {Promise<object>} The backend's response.
 */
export const updateTableInfo = async (tableInfo) => {
    try {
        const response = await fetch(`${API_BASE_URL}/admin/updateCalendarInfo`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
            body: JSON.stringify(tableInfo),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Table update failed' }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Table update failed:", e);
        throw e;
    }
};


/**
 * **NEW ADMIN FUNCTION**
 * Posts the new calendar metadata to the backend.
 * @param {object} calendarData The calendar entry details.
 * @returns {Promise<object>} The backend's response.
 */
/**
 * **UPDATED**
 * Adds a new calendar entry to the database table.
 * @param {object} payload - The calendar data with snake_case keys.
 * @returns {Promise<object>} The backend's response.
 */
export const addCalendarTableEntry = async (payload) => {
    // **FIX:** Transform the payload to match the Pydantic model's aliases.
    const transformedPayload = {
        'Calendar ID': payload.calendar_id,
        'Calendar Year': payload.calendar_year,
        'Client Name': payload.client_name,
        'Client Code': payload.client_code,
        'File Name': payload.file_name,
        'Status': payload.status,
        'Timestamp': payload.timestamp,
        'Uploaded By': payload.uploaded_by,
        'Last Updated By': payload.last_updated_by,
    };

    try {
        const response = await fetch(`${API_BASE_URL}/admin/calendartable`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
            body: JSON.stringify(transformedPayload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: `HTTP error! status: ${response.status}` }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Failed to add calendar table entry:", e);
        throw e;
    }
};


/**
 * **NEW ADMIN FUNCTION**
 * Updates an existing calendar entry.
 * @param {object} calendarData - The full payload for the calendar update.
 * @returns {Promise<object>} The backend's response.
 */
export const updateCalendar = async (calendarData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/admin/updateCalendar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
            body: JSON.stringify(calendarData),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Failed to update calendar' }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Failed to update calendar:", e);
        throw e;
    }
};

/**
 * **NEW ADMIN FUNCTION**
 * Adds or deletes clients by sending a list of modifications.
 * @param {Array<object>} modifications - An array of client objects to update.
 * Each object should have ClientName, ClientCode, and an 'option' ('add' or 'delete').
 * @returns {Promise<object>} The backend's response.
 */
export const updateClients = async (modifications) => {
    try {
        const response = await fetch(`${API_BASE_URL}/admin/updateClients`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'accept': 'application/json' },
            body: JSON.stringify(modifications),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Failed to update clients' }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Failed to update clients:", e);
        throw e;
    }
};

/**
 * **NEW FUNCTION**
 * Creates a new hot topic entry.
 * @param {object} payload The hot topic data from the form.
 * @returns {Promise<object>} The backend's response.
 */
export const createHotTopic = async (payload) => {
    try {
        const response = await fetch(`${API_BASE_URL}/admin/createHottopic`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Failed to create hot topic' }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Failed to create hot topic:", e);
        throw e;
    }
};

/**
 * **NEW FUNCTION**
 * Updates an existing hot topic entry.
 * @param {object} payload The hot topic data from the form.
 * @returns {Promise<object>} The backend's response.
 */
export const updateHotTopic = async (payload) => {
    try {
        const response = await fetch(`${API_BASE_URL}/admin/updateHottopic`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Failed to update hot topic' }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Failed to update hot topic:", e);
        throw e;
    }
};


/**
 * **NEW FUNCTION**
 * Fetches all calendar data from the backend.
 * @returns {Promise<object>} The backend's response containing the calendar data.
 */
export const getCalendarData = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/getdetails/getCalendarData`);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch calendar data' }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Failed to fetch calendar data:", e);
        throw e;
    }
};

/**
 * **NEW FUNCTION**
 * Deletes a specific calendar entry.
 * @param {object} payload - Contains calendar_id and file_name.
 * @returns {Promise<object>} The backend's response.
 */
export const deleteCalendar = async (payload) => {
    try {
        const response = await fetch(`${API_BASE_URL}/admin/deleteCalendar`, {
            method: 'POST', // Or 'DELETE' if your backend uses that
            headers: {
                'Content-Type': 'application/json',
                'accept': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Failed to delete calendar' }));
            throw new Error(errorData.detail || errorData.message);
        }

        return await response.json();
    } catch (e) {
        console.error("Failed to delete calendar:", e);
        throw e;
    }
};


/**
 * **UPDATED FUNCTION**
 * Fetches hot topics data from the backend.
 * @param {boolean} [filterActive=false] - If true, fetches only currently valid topics.
 * @returns {Promise<Array>} The backend's response containing the hot topics data.
 */
export const getHotTopics = async (filterActive = false) => {
    try {
        // Append the filter_active query parameter to the URL
        const url = `${API_BASE_URL}/getdetails/getHottopics?filter_active=${filterActive}`;
        
        const response = await fetch(url);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch hot topics' }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Failed to fetch hot topics:", e);
        throw e;
    }
};

/**
 * **NEW FUNCTION**
 * Deletes a specific hot topic.
 * @param {string} hottopic_id - The ID of the hot topic to delete.
 * @returns {Promise<object>} The backend's response.
 */
export const deleteHotTopic = async (payload) => {
    try {
        const response = await fetch(`${API_BASE_URL}/admin/deleteHottopic`, {
            method: 'POST', 
            headers: {
                'Content-Type': 'application/json',
                'accept': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Failed to delete hot topic' }));
            throw new Error(errorData.detail || errorData.message);
        }

        return await response.json();
    } catch (e) {
        console.error("Failed to delete hot topic:", e);
        throw e;
    }
};


export const createFaq = async (payload) => {
    try {
        const response = await fetch(`${API_BASE_URL}/admin/createFaq`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Failed to create FAQ' }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Failed to create FAQ:", e);
        throw e;
    }
};

export const updateFaq = async (payload) => {
    try {
        const response = await fetch(`${API_BASE_URL}/admin/updateFaq`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Failed to update FAQ' }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Failed to update FAQ:", e);
        throw e;
    }
};

export const getFaqs = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/getdetails/getFaqs`);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch FAQs' }));
            throw new Error(errorData.detail);
        }
        return await response.json();
    } catch (e) {
        console.error("Failed to fetch FAQs:", e);
        throw e;
    }
};

export const deleteFaq = async (payload) => {
    try {
        const response = await fetch(`${API_BASE_URL}/admin/deleteFaq`, {
            method: 'POST', 
            headers: {
                'Content-Type': 'application/json',
                'accept': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Failed to delete FAQ' }));
            throw new Error(errorData.detail || errorData.message);
        }

        return await response.json();
    } catch (e) {
        console.error("Failed to delete FAQ:", e);
        throw e;
    }
};
